import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { CREATE_OPTION_GROUP, UPDATE_OPTION_GROUP } from '../graphql/mutations'
import { GET_OPTION_GROUPS } from '../graphql/product-queries'
import { CreateOptionGroupData, UpdateOptionGroupData, OptionGroup } from '../types/product'
import toast from 'react-hot-toast'

interface OptionGroupFormProps {
  optionGroup?: OptionGroup
  isEditing?: boolean
}

const OptionGroupForm = ({ optionGroup, isEditing = false }: OptionGroupFormProps) => {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState<CreateOptionGroupData>({
    name: '',
    description: null,
    min_select: 0,
    max_select: 1,
    ask_during_add: true,
    active: true,
    sort_order: null
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const [createOptionGroup, { loading: createLoading }] = useMutation(CREATE_OPTION_GROUP, {
    onCompleted: () => {
      toast.success('Option group created successfully')
      navigate('/option-groups')
    },
    onError: (error) => {
      toast.error(`Failed to create option group: ${error.message}`)
      if (error.message.includes('already exists')) {
        setErrors({ name: 'An option group with this name already exists' })
      }
    }
  })

  const [updateOptionGroup, { loading: updateLoading }] = useMutation(UPDATE_OPTION_GROUP, {
    onCompleted: () => {
      toast.success('Option group updated successfully')
      navigate('/option-groups')
    },
    onError: (error) => {
      toast.error(`Failed to update option group: ${error.message}`)
      if (error.message.includes('already exists')) {
        setErrors({ name: 'An option group with this name already exists' })
      }
    }
  })

  useEffect(() => {
    if (optionGroup && isEditing) {
      setFormData({
        name: optionGroup.name,
        description: optionGroup.description ?? null,
        min_select: optionGroup.min_select,
        max_select: optionGroup.max_select,
        ask_during_add: optionGroup.ask_during_add,
        active: optionGroup.active,
        sort_order: optionGroup.sort_order
      })
    }
  }, [optionGroup, isEditing])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (Number(formData.min_select ?? 0) < 0) {
      newErrors.min_select = 'Minimum selections must be 0 or greater'
    }

    if (Number(formData.max_select ?? 0) < 1) {
      newErrors.max_select = 'Maximum selections must be 1 or greater'
    }

    if (Number(formData.min_select ?? 0) > Number(formData.max_select ?? 0)) {
      newErrors.max_select = 'Maximum selections must be greater than or equal to minimum selections'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      const coercedData: CreateOptionGroupData | UpdateOptionGroupData = {
        ...formData,
        min_select: Number(formData.min_select),
        max_select: Number(formData.max_select),
        sort_order: formData.sort_order !== null && formData.sort_order !== undefined
          ? Number(formData.sort_order)
          : null
      }

      if (isEditing && optionGroup) {
        const updateData: UpdateOptionGroupData = { ...coercedData }
        await updateOptionGroup({
          variables: {
            id: Number(optionGroup.id),
            data: updateData
          },
          refetchQueries: [GET_OPTION_GROUPS]
        })
      } else {
        await createOptionGroup({
          variables: { data: coercedData },
          refetchQueries: [GET_OPTION_GROUPS]
        })
      }
    } catch (error) {
      // Error handling is done in the mutation callbacks
    }
  }

  const handleInputChange = (field: keyof CreateOptionGroupData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const loading = createLoading || updateLoading

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Option Group' : 'Create Option Group'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing 
              ? 'Update the option group settings below'
              : 'Configure the option group settings for product customization'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Coriander Topping"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., How spicy do you want your dish?"
              disabled={loading}
            />
          </div>

          {/* Selection Rules */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="min_select" className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Selections *
              </label>
              <input
                type="number"
                id="min_select"
                min="0"
                value={formData.min_select}
                onChange={(e) => handleInputChange('min_select', parseInt(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.min_select ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.min_select && (
                <p className="mt-1 text-sm text-red-600">{errors.min_select}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Minimum number of options customer must select</p>
            </div>

            <div>
              <label htmlFor="max_select" className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Selections *
              </label>
              <input
                type="number"
                id="max_select"
                min="1"
                value={formData.max_select}
                onChange={(e) => handleInputChange('max_select', parseInt(e.target.value) || 1)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.max_select ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.max_select && (
                <p className="mt-1 text-sm text-red-600">{errors.max_select}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Maximum number of options customer can select</p>
            </div>
          </div>

          {/* Sort Order */}
          <div>
            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-2">
              Sort Order
            </label>
            <input
              type="number"
              id="sort_order"
              value={formData.sort_order || ''}
              onChange={(e) => handleInputChange('sort_order', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional - lower numbers appear first"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">Optional - controls display order (lower numbers appear first)</p>
          </div>

          {/* Checkboxes */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="ask_during_add"
                checked={formData.ask_during_add}
                onChange={(e) => handleInputChange('ask_during_add', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="ask_during_add" className="ml-2 block text-sm text-gray-700">
                Ask during add to cart
              </label>
            </div>
            <p className="ml-6 text-xs text-gray-500">
              Show this option group when customers add products to cart
            </p>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => handleInputChange('active', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                disabled={loading}
              />
              <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                Active
              </label>
            </div>
            <p className="ml-6 text-xs text-gray-500">
              Make this option group available for use
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/option-groups')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEditing ? 'Update Option Group' : 'Create Option Group'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default OptionGroupForm
